import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle, Sparkles, Truck, Calendar, GitBranch, Leaf, Cpu, Users, Tag, Eye, Package } from 'lucide-react';
import { services, whyChooseUs, testimonials } from '../mock';
import Process from "./Process";
const Home = () => {

  /* 🔁 HERO SLIDER LOGIC */
  const heroImages = [
    "/images/pu.png",
    "/images/school.png",
    "/images/higher edu.png",
  ];

  const [currentHeroSlide, setCurrentHeroSlide] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 5) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-sky-50">

      {/* Hero Section */}
      <section className="relative w-full overflow-hidden bg-[#0e4a8f] pt-0 pb-6 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 lg:gap-12 items-center h-full">

            {/* LEFT COLUMN: Content */}
            <div className="text-left space-y-3 z-30 relative pt-6 pb-4 lg:pt-0 lg:pb-0 w-full">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/40 backdrop-blur-sm text-sky-100 text-xs sm:text-sm font-medium shadow-sm">
                <span className="text-sky-200">✦</span>
                Student Trusted Laundry Partner
              </div>

              {/* Title */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-white/90 mb-0">
                  The No.1 Trusted
                </h3>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white -mt-1">
                  Laundry Solutions{" "}
                  <span className="text-sky-400">for Large-Scale Operations</span>
                </h1>
              </div>

              {/* Description */}
              <p className="text-sm sm:text-base lg:text-lg text-sky-100 max-w-xl leading-relaxed">
                We handle large-volume Laundry with care for Residential Schools, Colleges, Hospitals, and Hotels across India.
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild size="lg" className="bg-sky-500 hover:bg-sky-600 text-white border-0 px-6 h-11 text-base rounded-lg shadow-lg w-full sm:w-auto">
                  <Link to="/contact">
                    Get a Quote
                  </Link>
                </Button>
                <Button asChild size="lg" className="bg-sky-500 hover:bg-sky-600 text-white border-0 px-6 h-11 text-base rounded-lg shadow-lg w-full sm:w-auto">
                  <Link to="/process">
                    See How We Work
                  </Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-3 w-full">
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">500+</div>
                  <div className="text-[10px] sm:text-xs text-sky-200 mt-1 leading-tight">Institutions<br />Served</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">99%</div>
                  <div className="text-[10px] sm:text-xs text-sky-200 mt-1 leading-tight">Client<br />Satisfaction</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">10K+</div>
                  <div className="text-[10px] sm:text-xs text-sky-200 mt-1 leading-tight">Garments<br />Per Day</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">20+</div>
                  <div className="text-[10px] sm:text-xs text-sky-200 mt-1 leading-tight">Years of<br />Experience</div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Slider Images */}
            <div className="relative h-[220px] sm:h-[300px] md:h-[380px] lg:h-[540px] w-full flex items-center justify-center lg:justify-end">
              {heroImages.map((img, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center justify-center ${index === currentHeroSlide ? "opacity-100 scale-100" : "opacity-0 scale-95"
                    }`}
                >
                  <img
                    src={img}
                    alt="Laundry Service"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-8 lg:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-3 text-center">
              About Venus Fabcare
            </h2>
            <p className="text-sm md:text-base text-gray-600 text-center max-w-3xl mx-auto">
              Venus Fabcare supports institutions across India with reliable laundry services built for scale. We follow a practical, step-by-step approach where garments are carefully sorted, cleaned, finished, and delivered without shortcuts.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 justify-center">
            {[
              { icon: Leaf, title: 'Eco-Friendly', desc: 'Using eco-friendly detergents' },
              { icon: Cpu, title: 'Modern Equipment', desc: 'State-of-the-art machinery' },
              { icon: Sparkles, title: 'Fabric Knowledge', desc: 'Expert handling of all fabrics' },
              { icon: CheckCircle, title: 'Quality Focus', desc: 'Maintaining hygiene standards' }
            ].map((item, index) => (
              <Card key={index} className="border-blue-100 bg-blue-100 hover:border-sky-300 transition-all hover:shadow-lg">
                <CardContent className="p-3 text-center flex flex-col items-center justify-center">
                  <div className="mb-1 flex justify-center text-sky-600">
                    <item.icon className="h-6 w-6 md:h-8 md:w-8" />
                  </div>
                  <h3 className="font-semibold text-sm md:text-base mb-1 text-gray-900">{item.title}</h3>
                  <p className="text-xs text-gray-600 leading-snug">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-8 lg:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-2">
              Our Expert Laundry Services
            </h2>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
              Comprehensive laundry solutions tailored for different institutional needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-stretch justify-center">
            {services.map((service) => (
              <Card
                key={service.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden group flex flex-col"
              >
                {/* FIXED: increased image height from h-48 md:h-56 to h-64 md:h-80 */}
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-sky-900/60 to-transparent"></div>
                </div>

                <CardContent className="p-4 flex flex-col flex-1">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 text-left">
                    {service.title}
                  </h3>

                  <p className="text-sm text-gray-600 mb-3 text-left">
                    {service.description}
                  </p>

                  <ul className="space-y-1 mb-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-sky-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto w-full">
                    <Button
                      asChild
                      variant="outline"
                      className="w-full text-sky-600 border-sky-600 hover:bg-sky-50 text-sm"
                    >
                      <Link to={`/services/${service.slug}`}>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-10 lg:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900">
              Why Choose Venus Fabcare
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors flex flex-col items-center justify-start w-[calc(50%-8px)] lg:w-[calc(25%-12px)]">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-600 text-white rounded-full mb-3">
                  {(() => {
                    const Icon = {
                      truck: Truck,
                      calendar: Calendar,
                      gitBranch: GitBranch,
                      leaf: Leaf,
                      cpu: Cpu,
                      users: Users,
                      tag: Tag,
                      eye: Eye,
                      package: Package
                    }[item.icon] || Package;
                    return <Icon className="h-6 w-6" />;
                  })()}
                </div>
                <h3 className="font-semibold text-sm mb-1 text-gray-900">{item.title}</h3>
                <p className="text-xs text-gray-600 leading-snug">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-blue-900">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <Card className="bg-gradient-to-br from-blue-900 to-sky-700 border-0 h-full">
                  <CardContent className="p-6 flex flex-col justify-between text-center text-white h-full">
                    <p className="text-white/95 mb-6 italic leading-relaxed text-sm">
                      "{testimonial.text}"
                    </p>
                    <div className="border-t border-white/30 pt-4">
                      <p className="font-semibold text-white">{testimonial.name}</p>
                      <p className="text-xs text-white/75">{testimonial.organization}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-blue-900 to-sky-700 text-white p-8 md:p-12 lg:p-16 shadow-2xl">
            <div className="text-center">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6">
                Ready to Partner with Venus Fabcare?
              </h2>
              <p className="text-lg lg:text-xl mb-8 text-sky-100">
                Let's handle laundry, while your institution focuses on its core mission
              </p>
              <Button asChild size="lg" className="bg-white text-sky-600 hover:bg-sky-50 text-base px-8 py-6 text-lg">
                <Link to="/contact">
                  Contact Us Today
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;